import * as THREE from 'three';
import type { City, Place } from '../world/city';
import type { Vec } from '../destruction/structure';
import { DOCK_TOP, type Dock } from '../world/marina';
import { terrainHeight, WATER_LEVEL } from '../world/terrain';
import { BOAT_SPECS, BOAT_FAMILIES } from '../render/boats';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person, TrafficVehicle } from './population';
import { Reservations } from './reservations';

interface Leg {
  point: THREE.Vector3;
  reverse?: boolean;
}
interface Ship {
  channel?: Dock;
  boat: TrafficVehicle;
  captain: Person;
  from: Dock;
  destination: Dock;
  slot: number;
  state: 'docked' | 'sailing' | 'approach' | 'unavailable';
  route: Leg[];
  reason: string;
  dwell: number;
  trips: number;
  cargo: number;
  parcels: number;
  phase: '' | 'leave' | 'walk' | 'work' | 'return' | 'board';
  timer: number;
}
interface Ticket {
  person: Person;
  dock: Dock;
  ship?: Ship;
  state: 'waiting' | 'walking' | 'boarding' | 'aboard' | 'alighting';
  time: number;
  progress: number;
  seat: number;
  waiting?: Vec;
}
const v = (p: Vec) => new THREE.Vector3(p.x, p.y, p.z);
const angle = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));
const WATER_Y = WATER_LEVEL - 0.565;

/** Four original craft. Berths, crew, cargo and passenger identity persist on/off camera. */
export class Marine {
  ships: Ship[] = [];
  tickets = new Map<string, Ticket>();
  time = 0;
  boardings = 0;
  arrivals = 0;
  private epoch = 0;
  private leases: Reservations;
  log: { time: number; ship?: string; person?: string; action: string }[] = [];
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly pop: Population,
  ) {
    this.leases = new Reservations(() => physics.life.epoch);
    this.reset();
  }
  private record(action: string, s?: Ship, p?: Person) {
    this.log.push({ time: this.time, ship: s?.boat.id, person: p?.id, action });
    if (this.log.length > 512) this.log.shift();
  }
  private resource(d: Dock, slot: number) {
    return `${d.id}:berth:${slot}`;
  }
  private live(id: number) {
    const owner = this.physics.cellOwner.get(id);
    return (
      owner !== undefined && !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
    );
  }
  private berth(s: Ship, d = s.destination, slot = s.slot) {
    const a = d.slots[slot];
    return new THREE.Vector3(a.edge + a.side * (s.boat.width / 2 + 0.3), WATER_Y, a.z);
  }
  private door(s: Ship) {
    const a = s.destination.slots[s.slot];
    return new THREE.Vector3(a.edge - a.side * 0.5, DOCK_TOP, a.z);
  }
  private aboard(s: Ship, seat = -1) {
    return s.boat.p
      .clone()
      .add(
        new THREE.Vector3(
          seat < 0 ? 0 : seat % 2 ? -0.48 : 0.48,
          0.9,
          seat < 0 ? -s.boat.length * 0.23 : 0.4 + Math.floor(seat / 2) * 0.8,
        ).applyQuaternion(s.boat.q),
      );
  }
  clear() {
    for (const t of this.tickets.values()) {
      t.person.hidden = false;
      t.person.controlled = false;
      t.person.seatBlend = undefined;
      t.person.seatHeight = undefined;
    }
    for (const s of this.ships) {
      s.captain.controlled = false;
      s.captain.hidden = false;
      s.captain.carry = '';
    }
    this.ships = [];
    this.tickets.clear();
    this.leases.clear();
    this.log = [];
    this.time = this.boardings = this.arrivals = 0;
  }
  reset() {
    this.clear();
    this.epoch = this.physics.life.epoch;
    const docks = this.city.marina?.docks;
    if (!docks?.length) return;
    docks.forEach((d, i) => {
      d.stock = i ? 0 : 24;
      d.received = 0;
    });
    for (const [i, boat] of this.pop.cars.filter((c) => c.kind === 'boat').entries()) {
      const dock = docks[i < 2 ? 0 : 1],
        slot = i % 2;
      const captain =
        this.pop.people.find((p) => p.id === `crew:${boat.id}`) ??
        this.pop.createCrew(`crew:${boat.id}`, boat.p);
      captain.name =
        boat.boatFamily === 'work'
          ? 'Packet boat dock worker'
          : boat.boatFamily === 'skiff'
            ? 'Coastal angler'
            : 'Coastal boat captain';
      captain.controlled = true;
      captain.hidden = false;
      captain.state = 'sitting';
      captain.seatHeight = 0.36;
      captain.seatBlend = 1;
      const s: Ship = {
        boat,
        captain,
        from: dock,
        destination: dock,
        slot,
        state: 'docked',
        route: [],
        reason: 'Moored at original berth',
        dwell: 25 + i * 8,
        trips: 0,
        cargo: 0,
        parcels: 0,
        phase: '',
        timer: 0,
      };
      boat.controlled = true;
      boat.state = 'docked';
      boat.speed = BOAT_SPECS[boat.boatFamily ?? BOAT_FAMILIES[i]].speed;
      captain.p.copy(this.aboard(s));
      captain.previous.copy(captain.p);
      this.ships.push(s);
      this.leases.acquire(this.resource(dock, slot), boat.id, 0, 1, 60);
    }
    const residents = [...(this.pop.civil?.routines.values() ?? [])]
      .filter((r) => ['resident', 'visitor'].includes(r.role) && r.home)
      .sort(
        (a, b) =>
          Math.hypot(a.person.p.x - 330, a.person.p.z - 615) -
          Math.hypot(b.person.p.x - 330, b.person.p.z - 615),
      )
      .slice(0, 8);
    for (const r of residents) {
      r.role = 'ferry-traveller';
      r.phase = 0;
    }
  }
  owns(boat: TrafficVehicle) {
    return this.ships.some((s) => s.boat === boat);
  }
  waitAtDock(person: Person, gate: Place): boolean {
    if (this.tickets.has(person.id)) return true;
    const dock = this.city.marina?.docks.find((d) => d.gates.some((p) => p === gate));
    if (!dock || !this.pop.civil?.network.available(gate) || person.p.distanceTo(v(gate)) > 0.45)
      return false;
    const used = new Set([...this.tickets.values()].filter((t) => t.dock === dock).map((t) => t.waiting));
    const waiting = dock.waiting.find((p) => !used.has(p));
    if (!waiting) return false;
    person.controlled = true;
    this.tickets.set(person.id, {
      person,
      dock,
      state: 'waiting',
      time: this.time,
      progress: 0,
      seat: 0,
      waiting,
    });
    this.record('Original resident waiting at an accessible ferry berth', undefined, person);
    return true;
  }
  private release(t: Ticket, reason: string) {
    this.tickets.delete(t.person.id);
    this.leases.release(t.person.id);
    t.person.hidden = false;
    t.person.controlled = false;
    t.person.seatHeight = undefined;
    t.person.seatBlend = undefined;
    const r = this.pop.civil?.routines.get(t.person.id);
    if (t.person.fatal || ['incapacitated', 'falling', 'flee'].includes(t.person.state)) {
      this.pop.civil?.reservations.release(t.person.id);
      if (r) {
        r.planVersion++;
        r.planPending = false;
        r.route = [];
        r.destination = undefined;
        r.reason = reason;
      }
      return;
    }
    if (r?.home) {
      r.phase = 1;
      this.pop.civil!.finishTransit(t.person, r.home, reason);
    }
  }
  private walk(p: Person, point: Vec, dt: number) {
    if (p.p.distanceTo(v(point)) < 0.22) return true;
    p.state = 'walk';
    p.seatBlend = undefined;
    p.seatHeight = undefined;
    const dock = this.city.marina!.docks.reduce((a, b) =>
      Math.abs(a.slots[0].edge - p.p.x) < Math.abs(b.slots[0].edge - p.p.x) ? a : b,
    );
    const guide = v(point);
    if (Math.abs(point.z - p.p.z) > 1.1 && (p.p.z > 663 || point.z > 663)) {
      guide.x = dock.slots[0].edge + 1.875 + (point.z < p.p.z ? 0.4 : -0.4);
      if (Math.abs(p.p.x - guide.x) > 0.2) guide.z = p.p.z;
    }
    if (this.pop.footClear(p, guide)) this.pop.walkTo(p, guide, dt, 1.5);
    else this.pop.yieldFoot(p, guide, dt);
    return false;
  }
  private plan(s: Ship) {
    const docks = this.city.marina!.docks,
      target = docks.find((d) => d !== s.destination)!;
    if (!this.leases.acquire(`${s.destination.id}:channel`, s.boat.id, this.time, 1, 60)) {
      s.reason = 'Waiting at berth for the shared harbour entrance';
      return;
    }
    s.channel = s.destination;
    this.leases.release(s.boat.id, this.resource(s.destination, s.slot));
    s.from = s.destination;
    s.destination = target;
    s.state = 'sailing';
    s.boat.state = 'sailing';
    const points: Leg[] = [{ point: s.boat.p.clone().setZ(708), reverse: true }];
    const cruiseZ = target === docks[0] ? 734 : 716;
    points.push({ point: s.boat.p.clone().setZ(cruiseZ), reverse: true });
    if (s.boat.boatFamily === 'launch') {
      const river = this.city.river!.points.filter((p) => p.z >= 365 && p.z <= 735);
      const last = river.at(-1)!;
      points.push({ point: new THREE.Vector3(last.x + 5, WATER_Y, 735) });
      for (const p of [...river].reverse()) points.push({ point: new THREE.Vector3(p.x + 5, WATER_Y, p.z) });
      for (const p of river) points.push({ point: new THREE.Vector3(p.x - 5, WATER_Y, p.z) });
      points.push({ point: new THREE.Vector3(last.x - 5, WATER_Y, 735) });
    }
    points.push({
      point: new THREE.Vector3(target.slots[0].edge + 40 + this.ships.indexOf(s) * 10, WATER_Y, cruiseZ),
    });
    points.push({
      point: new THREE.Vector3(
        target.slots[0].edge + 40 + this.ships.indexOf(s) * 10,
        WATER_Y,
        752 + this.ships.indexOf(s) * 9,
      ),
    });
    s.route = points;
    s.reason =
      s.boat.boatFamily === 'work'
        ? 'Carrying conserved parcels to the other pier'
        : s.boat.boatFamily === 'launch'
          ? 'River and coast excursion with the original passengers'
          : 'Coastal journey to a reserved pier';
    this.record('Departed pier after boarding and dock work', s);
  }
  private cargo(s: Ship, dt: number): boolean {
    if (s.boat.boatFamily !== 'work') return true;
    const p = s.captain,
      d = s.destination,
      loading = d === this.city.marina!.docks[0];
    if (!this.live(d.supplyCell)) {
      s.reason = 'Damaged parcel store; cargo handling suspended';
      return false;
    }
    if (!s.phase) {
      s.phase = 'leave';
      s.timer = 0;
      if (!loading && s.cargo) {
        p.carry = 'parcel';
        s.cargo = 0;
        s.boat.cargo = 0;
      }
    }
    if (s.phase === 'leave') {
      s.timer = Math.min(1.5, s.timer + dt);
      p.p.lerpVectors(this.aboard(s), this.door(s), s.timer / 1.5);
      p.state = 'alighting';
      p.seatBlend = undefined;
      if (s.timer >= 1.5) {
        s.phase = 'walk';
        s.timer = 0;
      }
      return false;
    }
    if (s.phase === 'walk' && this.walk(p, d.store, dt)) {
      s.phase = 'work';
      s.timer = 0;
    }
    if (s.phase === 'work') {
      p.state = loading ? 'collecting' : 'unloading';
      s.timer += dt;
      if (s.timer >= 8) {
        if (loading && d.stock > 0) {
          d.stock--;
          p.carry = 'parcel';
        } else if (!loading && p.carry) {
          d.received++;
          s.parcels++;
          s.boat.cargo = 0;
          p.carry = '';
          this.record('Original dock worker delivered conserved parcel', s, p);
        }
        s.phase = 'return';
      }
    }
    if (s.phase === 'return' && this.walk(p, this.door(s), dt)) {
      s.phase = 'board';
      s.timer = 0;
    }
    if (s.phase === 'board') {
      s.timer = Math.min(1.5, s.timer + dt);
      p.p.lerpVectors(this.door(s), this.aboard(s), s.timer / 1.5);
      p.state = 'boarding';
      if (s.timer >= 1.5) {
        if (p.carry) {
          s.cargo = 1;
          s.boat.cargo = 1;
          p.carry = '';
        }
        p.state = 'sitting';
        p.seatBlend = 1;
        p.seatHeight = 0.36;
        return true;
      }
    }
    return false;
  }
  update(dt: number) {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    for (const s of this.ships) {
      s.captain.previous.copy(s.captain.p);
      if (s.state === 'unavailable') continue;
      if (s.channel) {
        this.leases.acquire(`${s.channel.id}:channel`, s.boat.id, this.time, 1, 60);
        if (
          s.state === 'sailing' &&
          (Math.abs(s.boat.p.x - s.channel.slots[0].edge) > 30 || s.boat.p.z > 743)
        ) {
          this.leases.release(s.boat.id, `${s.channel.id}:channel`);
          s.channel = undefined;
        }
      }
      if (
        s.boat.state === 'wreck' ||
        s.captain.fatal ||
        ['incapacitated', 'falling', 'flee'].includes(s.captain.state)
      ) {
        s.state = 'unavailable';
        s.reason = 'Craft or crew disabled';
        this.leases.release(s.boat.id);
        if (s.boat.state === 'wreck' && s.captain.state !== 'incapacitated')
          this.pop.incapacitate(s.captain, s.boat.p, 2);
        s.captain.controlled = false;
        for (const t of [...this.tickets.values()].filter((t) => t.ship === s)) {
          if (t.state === 'aboard') this.pop.incapacitate(t.person, s.boat.p, 2);
          this.release(t, 'Ferry disrupted by damaged craft');
        }
        continue;
      }
      if (s.state === 'docked') {
        if (!this.live(s.destination.slots[s.slot].support)) {
          s.reason = 'Berth damaged; access suspended';
          continue;
        }
        this.leases.acquire(this.resource(s.destination, s.slot), s.boat.id, this.time, 1, 60);
        const handled = this.cargo(s, dt);
        if (s.boat.boatFamily !== 'work') s.captain.p.copy(this.aboard(s));
        const capacity = BOAT_SPECS[s.boat.boatFamily ?? 'launch'].seats;
        const moving = [...this.tickets.values()].some(
          (t) => t.ship === s && ['walking', 'boarding', 'alighting'].includes(t.state),
        );
        const aboard = [...this.tickets.values()].filter((t) => t.ship === s).length;
        const next = [...this.tickets.values()].find(
          (t) => t.dock === s.destination && t.state === 'waiting',
        );
        if (!moving && next && aboard < capacity && s.dwell > 0) {
          next.ship = s;
          next.state = 'walking';
          next.seat = aboard;
          next.person.controlled = true;
          next.person.detour = undefined;
        }
        if (!moving && handled) s.dwell -= dt;
        if (s.dwell <= 0 && !moving && handled) this.plan(s);
      } else s.captain.p.copy(this.aboard(s));
      if (s.state === 'approach' && !s.route.length) {
        const slot = s.destination.slots.findIndex(
          (a, i) =>
            this.live(a.support) &&
            this.leases.acquire(this.resource(s.destination, i), s.boat.id, this.time, 1, 60),
        );
        if (slot < 0) {
          s.reason = 'Waiting offshore for a live free berth';
          continue;
        }
        if (!this.leases.acquire(`${s.destination.id}:channel`, s.boat.id, this.time, 1, 60)) {
          this.leases.release(s.boat.id, this.resource(s.destination, slot));
          s.reason = 'Holding at assigned offshore anchorage for harbour clearance';
          continue;
        }
        s.channel = s.destination;
        s.slot = slot;
        const point = this.berth(s);
        // Leave the individual holding row sideways before joining the harbour
        // channel. A diagonal from a farther anchorage crosses nearer boats.
        const column = s.destination.slots[0].edge + 15;
        s.route = [
          { point: new THREE.Vector3(column, WATER_Y, s.boat.p.z) },
          { point: new THREE.Vector3(column, WATER_Y, 708) },
          { point: point.clone().setZ(708) },
          { point },
        ];
      }
      if (s.state === 'approach')
        this.leases.acquire(this.resource(s.destination, s.slot), s.boat.id, this.time, 1, 60);
    }
    for (const t of [...this.tickets.values()]) {
      const p = t.person,
        s = t.ship;
      p.previous.copy(p.p);
      if (p.fatal || ['incapacitated', 'falling', 'flee'].includes(p.state)) {
        this.release(t, 'Ferry journey interrupted by a hazard');
        continue;
      }
      if (
        s &&
        ['walking', 'boarding', 'alighting'].includes(t.state) &&
        !this.live(s.destination.slots[s.slot].support)
      ) {
        // Keep the person's actual position when a gangway disappears. The
        // boarding interpolation must not carry them over a newly opened hole.
        const ground = this.physics.ground(p.p, 0.35, 0.9);
        p.state = ground ? 'walk' : 'falling';
        if (!ground) {
          p.fallStart = p.p.y;
          p.velocity.set(0, 0, 0);
        }
        this.release(t, 'Damaged pier interrupted access to the boat');
        continue;
      }
      if (t.state === 'waiting') {
        if (this.time - t.time > 600 || !t.dock.slots.some((a) => this.live(a.support)))
          this.release(t, 'No accessible ferry available; returning home');
        else if (t.waiting && this.walk(p, t.waiting, dt)) p.state = 'waiting for boat';
        continue;
      }
      if (!s) continue;
      if (t.state === 'walking' && this.walk(p, this.door(s), dt)) {
        t.state = 'boarding';
        t.progress = 0;
      }
      if (t.state === 'boarding') {
        t.progress = Math.min(1.5, t.progress + dt);
        p.state = 'boarding';
        p.p.lerpVectors(this.door(s), this.aboard(s, t.seat), t.progress / 1.5);
        if (t.progress >= 1.5) {
          t.state = 'aboard';
          this.boardings++;
          this.record('Original passenger boarded boat', s, p);
        }
      }
      if (t.state === 'aboard') {
        p.p.copy(this.aboard(s, t.seat));
        p.state = 'sitting';
        p.seatHeight = 0.36;
        p.seatBlend = 1;
        if (s.state === 'docked' && s.destination !== t.dock) {
          t.state = 'alighting';
          t.progress = 0;
        }
      }
      if (t.state === 'alighting') {
        if (
          [...this.tickets.values()].some(
            (other) => other !== t && other.ship === s && other.state === 'alighting' && other.progress > 0,
          )
        )
          continue;
        t.progress = Math.min(1.5, t.progress + dt);
        p.state = 'alighting';
        p.seatBlend = undefined;
        p.p.lerpVectors(this.aboard(s, t.seat), this.door(s), t.progress / 1.5);
        if (t.progress >= 1.5) {
          this.arrivals++;
          this.record('Same passenger alighted at destination pier', s, p);
          this.release(t, 'Coastal boat trip complete; walking to original home');
        }
      }
    }
  }
  move(boat: TrafficVehicle, dt: number) {
    const s = this.ships.find((s) => s.boat === boat);
    if (!s) return;
    if (s.state === 'docked' || s.state === 'unavailable') {
      boat.actualSpeed = 0;
      return;
    }
    const leg = s.route[0];
    if (!leg) {
      boat.actualSpeed = 0;
      return;
    }
    const delta = leg.point.clone().sub(boat.p).setY(0),
      distance = delta.length(),
      dir = delta.normalize();
    const step = Math.min(distance, boat.speed * dt),
      next = boat.p.clone().addScaledVector(dir, step);
    const spec = BOAT_SPECS[boat.boatFamily ?? 'launch'];
    const target = Math.atan2(-dir.x, -dir.z) + (leg.reverse ? Math.PI : 0),
      yaw = boat.yaw + angle(boat.yaw, target) * Math.min(1, dt * 1.4),
      q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    const hit = !this.physics.occupiedBoxClear(
      { x: boat.p.x, y: boat.p.y + spec.height / 2, z: boat.p.z },
      { x: boat.width / 2, y: spec.height / 2, z: boat.length / 2 },
      q,
      dir.clone().multiplyScalar(Math.max(0.4, step)),
      boat.body,
    );
    const bed = this.city.terrain ? terrainHeight(this.city.terrain, next.x, next.z) : -6;
    if (hit || bed === undefined || bed > next.y - 0.02) {
      boat.actualSpeed = 0;
      boat.blockedBy = hit ? 'live structure, bridge clearance or another craft' : 'shallow water';
      s.reason = `Holding course: ${boat.blockedBy}`;
      return;
    }
    boat.p.copy(next);
    boat.p.y = WATER_Y + Math.sin(this.time * 1.2 + boat.variant) * 0.025;
    boat.q.copy(q);
    boat.yaw = yaw;
    boat.body.setNextKinematicTranslation(boat.p);
    boat.body.setNextKinematicRotation(boat.q);
    boat.actualSpeed = step / dt;
    boat.blockedBy = '';
    if (distance <= step + 0.05) {
      s.route.shift();
      if (!s.route.length) {
        if (s.state === 'sailing') {
          s.state = 'approach';
        } else {
          s.state = 'docked';
          if (s.channel) {
            this.leases.release(s.boat.id, `${s.channel.id}:channel`);
            s.channel = undefined;
          }
          boat.state = 'docked';
          s.trips++;
          s.dwell = 25;
          s.phase = '';
          s.timer = 0;
          this.record('Moored original craft at reserved berth', s);
        }
      }
    }
  }
  snapshot(): Record<string, number> {
    return {
      boatTrips: this.ships.reduce((n, s) => n + s.trips, 0),
      boatBoardings: this.boardings,
      boatArrivals: this.arrivals,
      boatParcels: this.ships.reduce((n, s) => n + s.parcels, 0),
      boatWaiting: [...this.tickets.values()].filter((t) => t.state === 'waiting').length,
    };
  }
  inspect() {
    return {
      ...this.snapshot(),
      docks: this.city.marina?.docks.map((d) => ({ id: d.id, stock: d.stock, received: d.received })),
      ships: this.ships.map((s) => ({
        id: s.boat.id,
        family: s.boat.boatFamily,
        state: s.state,
        reason: s.reason,
        destination: s.destination.id,
        slot: s.slot,
        position: s.boat.p.toArray(),
        crew: s.captain.id,
        crewPosition: s.captain.p.toArray(),
        phase: s.phase,
        cargo: s.cargo,
        trips: s.trips,
        route: s.route.length,
      })),
      tickets: [...this.tickets.values()].map((t) => ({
        person: t.person.id,
        state: t.state,
        boat: t.ship?.boat.id,
      })),
      log: this.log,
    };
  }
}
